import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { loadStripe } from '@stripe/stripe-js';
import { CustomInputComponent, ValidationRule } from '../../../custom-input/custom-input.component';
import { PaymentLinkSentModalComponent } from '../../../payment-link-sent-modal/payment-link-sent-modal.component';
import { PaymentService, CreatePaymentLinkRequest } from '../../../../services/payment.service';
import { ApiService } from '../../../../services/api.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { LoggerService } from '../../../../services/logger.service';
import { environment } from '../../../../../environments/environment';
import { TapToPayService, TapToPayContext } from '../../services/tap-to-pay.service';
import { TapToPayAndroidNativeService } from '../../../../services/tap-to-pay-android-native.service';
import { PaymentFormData, PaymentMethod } from '../../services/add-order-state.service';
import { PhoneValidator } from '../../../../shared/validators/phone.validator';
import {
  MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
  isMembershipActivationPending,
  isMembershipCheckoutAccepted,
  isPaymentRequiredError,
  isRejectedPaymentResponse,
  resolveMembershipFailureMessage,
} from '../../../../shared/utils/membership-payment-status.util';
import { PaymentMethodCode } from '../../../../shared/types/payment-method.enum';
import { AuthService } from '../../../../services/auth.service';
import { OrdersService } from '../../../../orders/services/orders.service';
import { getActiveUserAuthToken } from '../../../../shared/utils/auth-headers.util';
import { ReceiptPrinterService } from '../../../../services/receipt-printer.service';

export interface PriceBreakdown {
  subtotal: number;
  tax: number;
  total: number;
  label: string;
}

export interface PaymentSummary {
  packageName: string;
  customerName: string;
  vehicleYear: string | number;
  vehicleMake: string;
  vehicleModel: string;
}

/**
 * Step 4 — Payment.
 * Handles all payment methods: Tap to Pay, Stripe card, payment link,
 * cash, and membership modes (DRB, Prepaid, Everwash, Way.com).
 * Initialises Stripe Elements lazily when the step becomes visible
 * and tears them down on destroy.
 *
 * Reuses: CustomInputComponent, PaymentLinkSentModalComponent, TapToPayService.
 */
@Component({
  selector: 'app-payment-step',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomInputComponent, PaymentLinkSentModalComponent],
  templateUrl: './payment-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-step.component.scss'],
})
export class PaymentStepComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  readonly PaymentMethodCode = PaymentMethodCode;
  @Input() orderId = '';
  @Input() stripeAccountId = '';
  @Input() priceBreakdown: PriceBreakdown = { subtotal: 0, tax: 0, total: 0, label: '' };
  @Input() summary: PaymentSummary = {
    packageName: '',
    customerName: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
  };
  @Input() customerPhone = '';
  @Input() customerEmail = '';
  @Input() locationId = '';
  @Input() isMonthly = false;
  @Input() sectionTitle = 'Payment Details';
  /** Set to false to hide the section title + Show Summary header row (e.g. when embedded in a modal that already has its own title). */
  @Input() showHeader = true;

  @Output() paymentSuccess = new EventEmitter<void>();
  @Output() paymentFailed = new EventEmitter<string>();
  @Output() back = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  paymentForm: PaymentFormData = this.defaultPaymentForm();
  showOrderSummary = false;
  showMorePaymentMethods = false;
  showPaymentLinkSentModal = false;
  paymentLinkError = '';

  // Processing flags
  isProcessingPayment = false;
  isConfirmingCashPayment = false;
  isCreatingPaymentLink = false;

  // Tap to Pay overlay state
  get taptopayLoading(): boolean {
    return this.tapToPayService.taptopayLoading;
  }
  showPaymentFailedOverlay = false;
  paymentFailureMessage = '';
  tapToPayStatusText = 'Fetching Payment Status';
  tapToPayStatusSubText =
    "Please wait while we confirm the customer's Tap to Pay transaction. This usually takes just a few seconds.";

  // Stripe
  private stripe: any;
  private cardNumberElement: any;
  private cardExpiryElement: any;
  private cardCvcElement: any;
  stripeCardNumberComplete = false;
  stripeCardExpiryComplete = false;
  stripeCardCvcComplete = false;
  private stripeCardNumberError = '';
  private stripeCardExpiryError = '';
  private stripeCardCvcError = '';
  /** Avoid duplicate `createPaymentIntent` calls for the same order (legacy flow). */
  private cardIntentPreparedForOrderId = '';

  // Validation — matches customer-info-step: optional but must be a valid US phone when present
  readonly paymentPhoneValidation: ValidationRule[] = [
    { type: 'required', message: 'Phone Number is required' },
    {
      type: 'custom',
      value: (v: string) => {
        const t = (v ?? '').trim();
        return !t || PhoneValidator.usaPhone({ value: t } as any) === null;
      },
      message: (v: string) => {
        const t = (v ?? '').trim();
        if (!t) return 'Phone Number is required';
        const r = PhoneValidator.usaPhone({ value: t } as any);
        return r
          ? ((r as any)['usaPhone']?.message ?? 'Please enter a valid US phone number').trim()
          : '';
      },
    },
  ];

  constructor(
    private readonly paymentService: PaymentService,
    private readonly apiService: ApiService,
    private readonly messageService: NotificationService,
    private readonly tapToPayService: TapToPayService,
    private readonly tapToPayAndroidNative: TapToPayAndroidNativeService,
    private readonly authService: AuthService,
    private readonly logger: LoggerService,
    private readonly ordersService: OrdersService,
    private readonly receiptPrinterService: ReceiptPrinterService
  ) {}

  /**
   * Initializes the payment form and sets up Tap to Pay success/failure subscriptions for the current order.
   * @example
   * gOnInit()
   * void
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    this.paymentForm = this.defaultPaymentForm();
    if (this.customerPhone) this.paymentForm.phoneNumber = this.customerPhone;

    this.tapToPayService.paymentSuccess$.pipe(takeUntil(this.destroy$)).subscribe(({ orderId }) => {
      if (orderId && this.orderId && orderId.toString() === this.orderId.toString()) {
        this.tapToPayStatusText = 'Payment received';
        this.tapToPayStatusSubText = 'Preparing confirmation...';
        this.showPaymentFailedOverlay = false;
        this.paymentSuccess.emit();
        this.tapToPayService.finishTapToPaySuccessUi();
        this.tapToPayStatusText = 'Fetching Payment Status';
        this.tapToPayStatusSubText =
          "Please wait while we confirm the customer's Tap to Pay transaction. This usually takes just a few seconds.";
      }
    });

    this.tapToPayService.paymentFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ orderId, message }) => {
        if (orderId && this.orderId && orderId.toString() === this.orderId.toString()) {
          this.tapToPayStatusText = 'Fetching Payment Status';
          this.tapToPayStatusSubText =
            "Please wait while we confirm the customer's Tap to Pay transaction. This usually takes just a few seconds.";
          this.paymentFailureMessage = message;
          this.showPaymentFailedOverlay = true;
        }
      });

    // Admin/staff orders emit here when Pusher reports success on `order-{userId}` (notification
    // modal + this step may only subscribe to `order-{locationId}`). Close the payment-link-sent UI.
    this.ordersService.paymentSucceeded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ orderId: paidId }) => {
        if (!paidId || !this.orderId) return;
        if (String(paidId).trim() !== String(this.orderId).trim()) return;
        this.showPaymentLinkSentModal = false;
      });
  }

  ngAfterViewInit(): void {
    if (this.paymentForm.method === PaymentMethodCode.Card && this.orderId) {
      this.scheduleStripeMount();
    }
  }

  /**
   * Handles input changes to reset card intent state, remount Stripe when needed, and sync the customer phone into the payment form.
   * @example
   * gOnChanges(changes)
   * undefined
   * @param {SimpleChanges} changes - Angular SimpleChanges object containing updated input values.
   * @returns {void} Does not return a value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderId']) {
      this.cardIntentPreparedForOrderId = '';
    }
    if (changes['orderId']?.currentValue && this.paymentForm.method === PaymentMethodCode.Card) {
      this.scheduleStripeMount();
    }
    // Re-mount Stripe when the connected account ID becomes available (arrives after order fetch)
    if (
      changes['stripeAccountId']?.currentValue &&
      !changes['stripeAccountId'].isFirstChange() &&
      this.paymentForm.method === PaymentMethodCode.Card
    ) {
      this.scheduleStripeMount();
    }
    if (changes['customerPhone']?.currentValue) {
      this.paymentForm.phoneNumber = changes['customerPhone'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.unmountStripeElements();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  /** Android Tap to Pay shell injects `window.TapToPayAndroid.startPayment`. */
  get isTapToPayNativeHost(): boolean {
    return this.tapToPayAndroidNative.isAvailable;
  }

  get isStripeCardComplete(): boolean {
    return (
      this.stripeCardNumberComplete && this.stripeCardExpiryComplete && this.stripeCardCvcComplete
    );
  }

  get isCashDisabled(): boolean {
    return this.isMonthly;
  }

  get canProceedWithCurrentMethod(): boolean {
    switch (this.paymentForm.method) {
      case PaymentMethodCode.TapToPay:
        return true;
      case PaymentMethodCode.Card:
        return this.isStripeCardComplete;
      case PaymentMethodCode.Link:
        return !!this.paymentForm.phoneNumber && this.isValidUsPhone(this.paymentForm.phoneNumber);
      case PaymentMethodCode.Cash:
        return !this.isCashDisabled;
      default:
        return true;
    }
  }

  // ── Payment method selection ───────────────────────────────────────────────

  selectPaymentMethod(method: PaymentMethod): void {
    if (method === PaymentMethodCode.TapToPay && !this.tapToPayAndroidNative.isAvailable) {
      return;
    }
    const prev = this.paymentForm.method;
    this.paymentForm.method = method;
    if (method === PaymentMethodCode.Cash) {
      // Open drawer when staff selects Cash so they can take tender (not on Print Receipt).
      void this.receiptPrinterService.openCashDrawer();
    }
    if (prev !== PaymentMethodCode.Card && method === PaymentMethodCode.Card)
      this.scheduleStripeMount();
    if (prev === PaymentMethodCode.Card && method !== PaymentMethodCode.Card)
      this.unmountStripeElements();
    // Auto-fill customer phone when switching to payment link
    if (method === PaymentMethodCode.Link && !this.paymentForm.phoneNumber && this.customerPhone) {
      this.paymentForm.phoneNumber = this.customerPhone;
    }
  }

  toggleOrderSummary(): void {
    this.showOrderSummary = !this.showOrderSummary;
  }
  toggleMorePaymentMethods(): void {
    this.showMorePaymentMethods = !this.showMorePaymentMethods;
  }

  // ── Payment actions ────────────────────────────────────────────────────────

  onTapToPayClick(): void {
    if (!this.orderId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Ready',
        detail: 'Order is still being created. Please wait.',
      });
      return;
    }
    const ctx = this.buildTapToPayContext();
    this.tapToPayService.retryWithOrderId(this.orderId, ctx);
  }

  /**
   * Creates a Stripe card payment method, prepares the payment intent if needed, and confirms checkout on the backend.
   * @example
   * onCardCheckout()
   * void
   * @returns {Promise<void>} A promise that resolves when the card checkout flow has been handled.
   **/
  async onCardCheckout(): Promise<void> {
    if (!this.orderId || !this.isStripeCardComplete || this.isProcessingPayment) return;
    this.isProcessingPayment = true;
    this.logger.trackPayment('card_checkout_start', {
      orderId: this.orderId,
      isMonthly: this.isMonthly,
      washType: this.isMonthly ? 'monthly_membership' : 'single_wash',
      stripeAccountId: this.stripeAccountId || null,
      priceBreakdown: this.priceBreakdown,
      cardIntentPreparedForOrderId: this.cardIntentPreparedForOrderId,
      cardholderNamePresent: Boolean(this.paymentForm.cardholderName),
      billingAddressPresent: Boolean(this.paymentForm.billingAddress),
    });
    try {
      this.logger.trackPayment('create_payment_method_start', {
        orderId: this.orderId,
        isMonthly: this.isMonthly,
      });
      const { paymentMethod, error: pmError } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          name: this.paymentForm.cardholderName || '',
          address: { line1: this.paymentForm.billingAddress || '' },
        },
      });

      if (pmError) {
        this.isProcessingPayment = false;
        this.logger.trackPayment('create_payment_method_failed', {
          orderId: this.orderId,
          isMonthly: this.isMonthly,
          error: {
            type: pmError.type,
            code: pmError.code,
            message: pmError.message,
            decline_code: (pmError as any).decline_code,
          },
        });
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail:
            pmError.message || 'Could not create payment method. Please check your card details.',
        });
        return;
      }

      const paymentMethodId = (paymentMethod?.id ?? '').trim();
      this.logger.trackPayment('create_payment_method_success', {
        orderId: this.orderId,
        isMonthly: this.isMonthly,
        paymentMethodId,
        paymentMethodType: paymentMethod?.type,
        cardBrand: (paymentMethod as any)?.card?.brand,
        cardLast4: (paymentMethod as any)?.card?.last4,
        cardFunding: (paymentMethod as any)?.card?.funding,
      });
      if (!paymentMethodId) {
        this.isProcessingPayment = false;
        this.logger.trackPayment('create_payment_method_empty_id', {
          orderId: this.orderId,
          isMonthly: this.isMonthly,
        });
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: 'Could not create payment method. Please check your card details and try again.',
        });
        return;
      }

      if (this.cardIntentPreparedForOrderId !== this.orderId) {
        try {
          this.logger.trackPayment('create_payment_intent_start', {
            orderId: this.orderId,
            paymentMethodId,
            isMonthly: this.isMonthly,
            washType: this.isMonthly ? 'monthly_membership' : 'single_wash',
            amount: this.priceBreakdown?.total,
            priceBreakdown: this.priceBreakdown,
            stripeAccountId: this.stripeAccountId || null,
          });
          const intentResponse: any = await firstValueFrom(
            this.paymentService.createPaymentIntent(this.orderId, paymentMethodId)
          );
          this.cardIntentPreparedForOrderId = this.orderId;
          this.logger.trackPayment('create_payment_intent_success', {
            orderId: this.orderId,
            paymentMethodId,
            isMonthly: this.isMonthly,
            status: intentResponse?.data?.status ?? intentResponse?.status,
            paymentIntentId: intentResponse?.data?.payment_intent_id,
            clientSecretPresent: Boolean(intentResponse?.data?.client_secret),
            subscriptionId: intentResponse?.data?.subscription_id,
            alreadyPaid: intentResponse?.data?.already_paid,
            stripeAccountId: intentResponse?.data?.stripe_account_id,
            responseKeys: intentResponse?.data ? Object.keys(intentResponse.data) : [],
            rawMessage: intentResponse?.message,
          });
        } catch (err: any) {
          this.isProcessingPayment = false;
          const errBody = err?.error;
          this.logger.trackPayment('create_payment_intent_failed', {
            orderId: this.orderId,
            paymentMethodId,
            isMonthly: this.isMonthly,
            amount: this.priceBreakdown?.total,
            priceBreakdown: this.priceBreakdown,
            httpStatus: err?.status,
            errorMessage: errBody?.message || err?.message,
            errorCode: errBody?.code || errBody?.error_code || errBody?.status_code,
            errorType: errBody?.type || errBody?.error?.type,
            stripeError: errBody?.raw || errBody?.error || errBody,
            fullErrorBody: errBody,
          });
          this.logger.trackError('PaymentStep.createPaymentIntent', err, {
            orderId: this.orderId,
            paymentMethodId,
            isMonthly: this.isMonthly,
          });
          this.messageService.add({
            severity: 'error',
            summary: isPaymentRequiredError(err) ? 'Membership not activated' : 'Payment Error',
            detail: isPaymentRequiredError(err)
              ? resolveMembershipFailureMessage(err)
              : err?.error?.message ||
                err?.message ||
                'Could not prepare card payment. Please try again.',
          });
          return;
        }
      } else {
        this.logger.trackPayment('create_payment_intent_skipped_already_prepared', {
          orderId: this.orderId,
          paymentMethodId,
          isMonthly: this.isMonthly,
          cardIntentPreparedForOrderId: this.cardIntentPreparedForOrderId,
        });
      }

      if (this.isMonthly) {
        this.logger.trackPayment('membership_confirm_after_intent', {
          orderId: this.orderId,
          paymentMethodId,
        });
        await this.confirmMembershipCardPayment(paymentMethodId);
        return;
      }

      this.isProcessingPayment = false;
      this.confirmCardPaymentOnBackend(paymentMethodId);
      this.paymentSuccess.emit();
    } catch (err: any) {
      this.isProcessingPayment = false;
      this.logger.trackPayment('card_checkout_unhandled_error', {
        orderId: this.orderId,
        isMonthly: this.isMonthly,
        errorMessage: err?.error?.message ?? err?.message,
        error: err?.error ?? err,
      });
      this.logger.trackError('PaymentStep.onCardCheckout', err, {
        orderId: this.orderId,
        isMonthly: this.isMonthly,
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail:
          err?.error?.message ?? err?.message ?? 'Payment processing failed. Please try again.',
      });
    }
  }

  /**
   * Creates and sends a payment link for the current order using the entered phone number and optional email.
   * @example
   * nCreatePaymentLink()
   * void
   * @returns {void} This method does not return a value.
   **/
  onCreatePaymentLink(): void {
    if (!this.orderId || this.isCreatingPaymentLink) return;
    if (!this.paymentForm.phoneNumber || !this.isValidUsPhone(this.paymentForm.phoneNumber)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Please enter a valid phone number.',
      });
      return;
    }
    this.isCreatingPaymentLink = true;
    this.paymentLinkError = '';
    const emailTrim = (this.customerEmail ?? '').trim();
    const req: CreatePaymentLinkRequest = {
      order_id: this.orderId,
      phone_number: this.paymentForm.phoneNumber,
      ...(emailTrim ? { email: emailTrim } : {}),
    };
    this.paymentService
      .createPaymentLink(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isCreatingPaymentLink = false;
          if (res?.status && (res?.paymentLink?.success ?? true)) {
            this.showPaymentLinkSentModal = true;
          } else {
            this.paymentLinkError = res?.message ?? 'Failed to send payment link.';
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Link',
              detail: this.paymentLinkError,
            });
          }
        },
        error: err => {
          this.isCreatingPaymentLink = false;
          this.logger.trackError('PaymentStep.createPaymentLink', err);
          this.paymentLinkError =
            err?.error?.message ?? err?.message ?? 'Failed to send payment link.';
        },
      });
  }

  onConfirmCash(): void {
    this.confirmPaymentMode(PaymentMethodCode.Cash);
  }
  onConfirmDRB(): void {
    this.confirmPaymentMode('DRB Membership');
  }
  onConfirmPrepaid(): void {
    this.confirmPaymentMode('Prepaid');
  }
  onConfirmEverwash(): void {
    this.confirmPaymentMode('Everwash Membership');
  }
  onConfirmWayCom(): void {
    this.confirmPaymentMode('Way.com Membership');
  }

  /**
   * Confirms the selected payment mode for the current order and emits success on completion.
   * @example
   * confirmPaymentMode(PaymentMethodCode.Cash)
   * void
   * @param {PaymentMethodCode.Cash | 'DRB Membership' | 'Prepaid' | 'Everwash Membership' | 'Way.com Membership'} mode - The payment mode to confirm for the order.
   * @returns {void} No return value.
   **/
  confirmPaymentMode(
    mode:
      | PaymentMethodCode.Cash
      | 'DRB Membership'
      | 'Prepaid'
      | 'Everwash Membership'
      | 'Way.com Membership'
  ): void {
    if (!this.orderId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Order must be created first.',
      });
      return;
    }
    if (this.isConfirmingCashPayment) return;
    this.isConfirmingCashPayment = true;

    this.apiService
      .post(`order/confirm-payment-mode/${this.orderId}`, { payment_mode: mode })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isConfirmingCashPayment = false;
          this.paymentSuccess.emit();
        },
        error: err => {
          this.isConfirmingCashPayment = false;
          this.logger.trackError('PaymentStep.confirmPaymentMode', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Error',
            detail: err?.error?.message ?? err?.message ?? `Failed to confirm ${mode} payment.`,
          });
        },
      });
  }

  stopPaymentStatusPolling(): void {
    this.tapToPayService.stopStatusPolling();
  }

  retryTapToPayPayment(): void {
    this.showPaymentFailedOverlay = false;
    this.paymentFailureMessage = '';
    if (!this.orderId) return;
    this.tapToPayService.resetPaymentStatusToPendingThenRetry(
      this.orderId,
      this.buildTapToPayContext()
    );
  }

  resetPaymentTracking(): void {
    this.showPaymentFailedOverlay = false;
    this.paymentFailureMessage = '';
    this.tapToPayService.resetPaymentTracking();
  }

  onPaymentLinkSentClose(): void {
    this.showPaymentLinkSentModal = false;
  }

  onCancelPayment(): void {
    this.showPaymentLinkSentModal = false;
  }

  onResendPaymentLink(): void {
    this.onCreatePaymentLink();
  }

  // ── Stripe ─────────────────────────────────────────────────────────────────

  private async initializeStripe(): Promise<void> {
    try {
      const stripeKey = environment.stripePublicKey ?? (environment as any).stripe_key ?? '';
      this._lastStripeAccount = this.stripeAccountId;
      this.stripe = await loadStripe(stripeKey, {
        stripeAccount: this.stripeAccountId || undefined,
      });
    } catch (err) {
      this.logger.trackError('PaymentStep.initStripe', err);
    }
  }

  /**
   * Schedules mounting of Stripe card input elements, retrying until the required DOM nodes are available.
   * @example
   * scheduleStripeMount(10, 300)
   * void
   * @param {number} retries - Number of retry attempts to wait for the card input elements.
   * @param {number} delayMs - Delay in milliseconds before attempting to mount again.
   * @returns {void} Nothing is returned.
   **/
  private scheduleStripeMount(retries = 10, delayMs = 300): void {
    setTimeout(async () => {
      if (this.paymentForm.method !== PaymentMethodCode.Card) return;
      const cardNum = document.getElementById('card-number');
      const cardExp = document.getElementById('card-expiry');
      const cardCvc = document.getElementById('card-cvc');
      if (!cardNum || !cardExp || !cardCvc) {
        if (retries > 0) this.scheduleStripeMount(retries - 1, 300);
        return;
      }
      // Re-initialize Stripe if stripeAccountId changed since last init
      // (can happen when the order fetch returns a different account than the user's default)
      if (
        !this.stripe ||
        (this.stripeAccountId && this._lastStripeAccount !== this.stripeAccountId)
      ) {
        this.stripe = null as any;
        await this.initializeStripe();
      }
      this.mountStripeElements();
    }, delayMs);
  }

  /** Tracks the connected-account ID used in the last loadStripe call. */
  private _lastStripeAccount = '';

  /**
   * Mounts Stripe card input elements, re-creating any stale instances and wiring up validation listeners.
   * @example
   * mountStripeElements()
   * void
   * @returns {void} Does not return a value.
   **/
  private mountStripeElements(): void {
    if (!this.stripe) return;
    try {
      // Destroy stale element instances so they are re-created with the current Stripe object
      if (this.cardNumberElement) {
        try {
          this.cardNumberElement.destroy();
        } catch {
          /* noop */
        }
        this.cardNumberElement = null;
        this.cardExpiryElement = null;
        this.cardCvcElement = null;
      }
      const elements = this.stripe.elements();
      const style = {
        base: { fontSize: '14px', color: '#32325d', '::placeholder': { color: '#aab7c4' } },
      };
      this.cardNumberElement = elements.create('cardNumber', { style });
      this.cardExpiryElement = elements.create('cardExpiry', { style });
      this.cardCvcElement = elements.create('cardCvc', { style });
      this.cardNumberElement.mount('#card-number');
      this.cardExpiryElement.mount('#card-expiry');
      this.cardCvcElement.mount('#card-cvc');
      this.setupStripeListeners(this.cardNumberElement, 'card-number-error');
      this.setupStripeListeners(this.cardExpiryElement, 'card-expiry-error');
      this.setupStripeListeners(this.cardCvcElement, 'card-cvc-error');
    } catch (err) {
      this.logger.trackError('PaymentStep.mountStripe', err);
    }
  }

  private unmountStripeElements(): void {
    try {
      this.cardNumberElement?.unmount();
      this.cardExpiryElement?.unmount();
      this.cardCvcElement?.unmount();
    } catch {
      /* noop */
    }
  }

  /**
   * Sets up Stripe event listeners to display and persist validation errors for a payment element.
   * @example
   * setupStripeListeners(element, 'card-error')
   * undefined
   * @param {any} element - The Stripe element instance to attach listeners to.
   * @param {string} errorId - The DOM element ID used to display the Stripe error message.
   * @returns {void} No return value.
   **/
  private setupStripeListeners(element: any, errorId: string): void {
    const errorEl = document.getElementById(errorId);
    if (!errorEl) return;

    element.on('change', (event: any) => {
      if (event.error) {
        errorEl.textContent = event.error.message;
        errorEl.style.display = 'block';
        this.storeStripeError(errorId, event.error.message);
        this.updateStripeCompletion(errorId, false);
      } else {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
        this.storeStripeError(errorId, '');
        this.updateStripeCompletion(errorId, event.complete ?? false);
      }
    });

    element.on('blur', (event: any) => {
      const stored = this.getStoredStripeError(errorId);
      const complete = this.getStripeCompletion(errorId);
      if (!complete && stored) {
        errorEl.textContent = stored;
        errorEl.style.display = 'block';
      } else if (complete) {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
      }
    });
  }

  private storeStripeError(id: string, msg: string): void {
    if (id === 'card-number-error') this.stripeCardNumberError = msg;
    else if (id === 'card-expiry-error') this.stripeCardExpiryError = msg;
    else if (id === 'card-cvc-error') this.stripeCardCvcError = msg;
  }

  private getStoredStripeError(id: string): string {
    if (id === 'card-number-error') return this.stripeCardNumberError;
    if (id === 'card-expiry-error') return this.stripeCardExpiryError;
    if (id === 'card-cvc-error') return this.stripeCardCvcError;
    return '';
  }

  private updateStripeCompletion(id: string, val: boolean): void {
    if (id === 'card-number-error') this.stripeCardNumberComplete = val;
    else if (id === 'card-expiry-error') this.stripeCardExpiryComplete = val;
    else if (id === 'card-cvc-error') this.stripeCardCvcComplete = val;
  }

  private getStripeCompletion(id: string): boolean {
    if (id === 'card-number-error') return this.stripeCardNumberComplete;
    if (id === 'card-expiry-error') return this.stripeCardExpiryComplete;
    if (id === 'card-cvc-error') return this.stripeCardCvcComplete;
    return false;
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Memberships wait for the backend's answer before reporting success. The one-time path below
   * can fire and forget because the charge either works or throws, but a membership can be refused
   * for recurring use even after the card authorizes, and emitting success on that would show an
   * active membership the customer does not have.
   *
   * A `pending` answer still counts as success: the money was taken and Stripe's webhook completes
   * the activation, which the order list picks up.
   */
  private async confirmMembershipCardPayment(paymentMethodId: string): Promise<void> {
    this.logger.trackPayment('confirm_membership_card_payment_start', {
      orderId: this.orderId,
      paymentMethodId,
      amount: this.priceBreakdown?.total,
      priceBreakdown: this.priceBreakdown,
    });
    try {
      const response: any = await firstValueFrom(
        this.paymentService.confirmCardPayment({
          order_id: this.orderId,
          payment_method_id: paymentMethodId,
        })
      );
      this.isProcessingPayment = false;
      this.logger.trackPayment('confirm_membership_card_payment_response', {
        orderId: this.orderId,
        paymentMethodId,
        status: response?.status,
        paymentStatus: response?.payment_status ?? response?.data?.payment_status,
        subscriptionStatus: response?.subscription_status ?? response?.data?.subscription_status,
        subscriptionId: response?.subscription_id ?? response?.data?.subscription_id,
        message: response?.message,
        rejected: isRejectedPaymentResponse(response),
        pending: isMembershipActivationPending(response),
        accepted: isMembershipCheckoutAccepted(response),
        responseKeys: response ? Object.keys(response) : [],
      });

      if (isRejectedPaymentResponse(response)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Membership not activated',
          detail: resolveMembershipFailureMessage(response),
          life: 6000,
        });
        return;
      }

      if (isMembershipActivationPending(response)) {
        this.messageService.add({
          severity: 'info',
          summary: 'Payment received',
          detail: MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
          life: 4000,
        });
        this.paymentSuccess.emit();
        return;
      }

      if (!isMembershipCheckoutAccepted(response)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Membership not activated',
          detail: resolveMembershipFailureMessage(response),
          life: 6000,
        });
        return;
      }

      this.paymentSuccess.emit();
    } catch (err: any) {
      this.isProcessingPayment = false;
      this.logger.trackPayment('confirm_membership_card_payment_failed', {
        orderId: this.orderId,
        paymentMethodId,
        httpStatus: err?.status,
        errorMessage: err?.error?.message || err?.message,
        errorBody: err?.error,
      });
      this.logger.trackError('PaymentStep.confirmMembershipCardPayment', err);
      this.messageService.add({
        severity: 'error',
        summary: isPaymentRequiredError(err) ? 'Membership not activated' : 'Payment Error',
        detail: isPaymentRequiredError(err)
          ? resolveMembershipFailureMessage(err)
          : err?.error?.message ||
            err?.message ||
            'Payment could not be completed. Please try again.',
        life: 6000,
      });
    }
  }

  private confirmCardPaymentOnBackend(paymentMethodId: string): void {
    // Do NOT use takeUntil(this.destroy$) here: paymentSuccess.emit() fires before this
    // call completes, which immediately destroys the component (modal closes) and would
    // cancel the in-flight HTTP request. This is intentionally fire-and-forget.
    this.paymentService
      .confirmCardPayment({ order_id: this.orderId, payment_method_id: paymentMethodId })
      .subscribe({
        error: (e: any) => this.logger.trackError('PaymentStep.confirmCardBackend', e),
      });
  }

  /**
   * Builds and returns the Tap to Pay context using current order, customer, and auth data.
   * @example
   * buildTapToPayContext()
   * { amount: '25.00', phoneNumber: '5551234567', email: 'user@example.com', locationId: 'loc_123', adminUserId: '42', washType: 'single_wash', packageId: '', vehicleId: '', authToken: 'jwt-token' }
   * @returns {TapToPayContext} The Tap to Pay context object containing payment and user details.
   **/
  private buildTapToPayContext(): TapToPayContext {
    const user = this.authService.user();
    return {
      amount: this.priceBreakdown.total.toString(),
      phoneNumber: this.paymentForm.phoneNumber || this.customerPhone,
      email: this.customerEmail,
      locationId: this.locationId,
      adminUserId: (user?.id ?? '').toString(),
      washType: this.isMonthly ? 'monthly_membership' : 'single_wash',
      packageId: '',
      vehicleId: '',
      stripeAccountId: this.stripeAccountId || undefined,
      authToken: getActiveUserAuthToken(),
    };
  }

  private defaultPaymentForm(): PaymentFormData {
    const method: PaymentMethod = this.isMonthly
      ? PaymentMethodCode.Card
      : this.tapToPayAndroidNative.isAvailable
        ? PaymentMethodCode.TapToPay
        : PaymentMethodCode.Cash;
    return { method, phoneNumber: '', cardholderName: '', billingAddress: '' };
  }

  isValidUsPhone(phone: string): boolean {
    if (!phone) return false;
    return PhoneValidator.usaPhone({ value: phone.trim() } as any) === null;
  }
}
